import React, { useContext } from 'react';
import { ModalContext, IModalContext } from 'contexts/modal';
import { UserContext, IUserContext } from 'contexts/user';
import DetailsTable from 'components/details-table';
import textTransform from 'utils/text-transform';
import { capitalizeWords } from '@zoocasa/node-kit/strings/capitalize';
import WalkScore from 'components/walk-score';
import styles from './style.module.scss';
import { getApproxAge } from 'utils/approx-age';
import { TryScrambleText } from 'utils/text-scrambler';
import { listingPageModalIds } from 'constants/test-constants';
import { useTranslation } from 'next-i18next';
import { SiteNameSpace } from 'constants/locale';
import _ from 'lodash';
import { validateLatitudeAndLongitude } from 'utils/geo_helper';

import type Listing from 'data/listing';

type Options = ({
  label: string;
  value?: string | number | boolean | null;
} | null)[];

interface Props {
  listing: Listing;
  isVisible: boolean;
  isCrawler: boolean;
  shouldScramble: boolean;
}

export default function Description({ listing, isVisible, isCrawler, shouldScramble }: Props) {
  const { isCondo, locker, isPrivate: listingIsPrivate, description, extras, miscellaneous,
    basement, driveway, mlsNum, providerId, isSignInAndTermsOfUseRequired } = listing;
  const { laundryLevel, centralVac, fireplaceStove, acreage, condoExp } = miscellaneous || {};
  const { isAuthenticated } = useContext(UserContext) as IUserContext;
  const { openModal } = useContext(ModalContext) as IModalContext;
  const isPrivate = !isAuthenticated && listingIsPrivate;
  const { t } = useTranslation(SiteNameSpace.Modal);

  const openTermsModal = () => {
    if (isPrivate || (isSignInAndTermsOfUseRequired && !isVisible && !isAuthenticated)) {
      openModal('login-registration', { warningMessage: t('login-registration.form.signinRequiredWarning') });
    } else if (!isVisible) {
      openModal('terms-of-use', { providerId });
    }
  };


  const fixEncoding = (text: string | undefined): string => {
    const textEncoded = isVisible ? _.unescape(text || '') : TryScrambleText(description, shouldScramble) || '';
    const textEncodedString = typeof textEncoded === 'string' ? textEncoded : '';
    return textEncodedString
      .replace(/â€™/g, '’')
      .replace(/â€œ/g, '“')
      .replace(/â€/g, '”')
      .replace(/â€“/g, '–')
      .replace(/â€”/g, '—');
  };

  const getDetails = () => {
    const properties: Options = [{ label: 'MLS® Number', value: TryScrambleText(mlsNum, shouldScramble) }];
    const remainingProperties = [
      !isCondo ? { label: 'Laundry Level', value: laundryLevel } : null,
      !isCondo ? { label: 'Central Vac', value: centralVac ? 'Yes' : 'No' } : null,
      !isCondo ? { label: 'Fireplace', value: fireplaceStove ? 'Yes' : 'No' } : null,
      !isCondo ? { label: 'Acreage', value: acreage } : null,
      isCondo ? { label: 'Locker', value: locker } : null,
    ];
    const unitProperties = getUnit();
    const buildingProperties = getBuilding();
    const buildingAmenitiesProperties = getBuildingAmenities();
    const utilities = !isCondo ? getUtilities() : null;

    return properties.concat(remainingProperties, unitProperties, buildingProperties, buildingAmenitiesProperties, utilities);
  };

  const getUnit = () => {
    if (isCondo) {
      const rootProperty = buildOptions(miscellaneous, 'ensuiteLaundry', 'balcony', 'furnished', 'laundryLevel');
      return rootProperty.concat([
        isCondo ? {
          label: 'Exposure', value: condoExp ? (condoExp as string).toUpperCase() : '',
        } : null,
      ]);
    } else {
      return null;
    }
  };

  const getBuilding = () => {
    const { approxAge, corpNum, stories, pets, garageSpaces } = miscellaneous || {};
    const properties = buildOptions(listing, 'exterior', 'garage');
    const remainingProperties = [
      { label: 'Approx. Age', value: approxAge ? getApproxAge(approxAge.toString()) : null },
      !isCondo ? { label: 'Basement', value: basement } : null,
      !isCondo ? { label: 'Driveway', value: driveway } : null,
      !isCondo ? { label: 'Garage Spaces', value: garageSpaces } : null,
      isCondo ? { label: 'Stories', value: stories } : null,
      isCondo ? { label: 'Pets', value: pets } : null,
      isCondo ? { label: 'Corp Number', value: corpNum } : null,
    ];
    return properties.concat(remainingProperties);
  };

  const getBuildingAmenities = () => {
    if (isCondo) {
      const amenities: Options = [];
      [1, 2, 3, 4, 5, 6].forEach(num => {
        const amenity = miscellaneous && miscellaneous[`bldgAmenities${num}Out`];
        if (amenity) {
          amenities.push({ label: amenity as string, value: 'Yes' });
        }
      });
      if (amenities.length) {
        return amenities;
      } else {
        return null;
      }
    } else {
      return null;
    }
  };

  const getIncludeInMaintenanceFee = () => {
    return buildOptions(miscellaneous, 'heatIncluded:Heat', 'hydroIncluded:Hydro', 'waterIncluded:Water', 'buildingInsuranceIncluded:Building Insurance', 'condoTaxesIncluded:Condo Taxes', 'cableTvIncluded:Cable TV', 'cacIncluded:Central Air', 'parkingIncluded:Parking Maintenance');
  };

  const getUtilities = () => buildOptions(listing, 'heat', 'ac:A/C', 'heatingFuel');

  const buildOptions = (keyPrefix: Record<string, any> | null, ...properties: string[]): Options => {
    return properties.map(property => {
      const [name, label] = property.split(':');
      const key = keyPrefix && keyPrefix[name];
      let value;
      if (key === true) {
        value = 'Yes';
      } else if (key === false) {
        value = 'No';
      } else {
        value = key;
      }
      return {
        label: label || capitalizeWords(textTransform(name, 'deCamelize')),
        value,
      };
    });
  };

  const latitude: number | undefined = listing?.position?.latitude;
  const longitude: number | undefined = listing?.position?.longitude;
  const showWalkScore = !isCrawler && latitude && longitude && validateLatitudeAndLongitude(latitude, longitude);

  return (
    <div className={styles.component}>
      <DetailsTable isPrivate={!isVisible}>
        <div className={styles.description}>
          {isVisible ?
            <section>{fixEncoding(description)}</section>
            :
            <>
              <span className='blur' data-testid={listingPageModalIds.descriptionDetailText}>{TryScrambleText(description, shouldScramble)}</span>
              <a onClick={() => openTermsModal()}>Show Description</a>
            </>
          }
        </div>
      </DetailsTable>
      {showWalkScore && <WalkScore latitude={listing.position.latitude} longitude={listing.position.longitude} />}
      <DetailsTable isPrivate={!isVisible}>
        <section>
          {getDetails().map((field, index) => {
            if (field) {
              return (
                <div key={index}>
                  <span>{field.label}</span>
                  <span>{TryScrambleText(field.value, shouldScramble)}</span>
                </div>
              );
            }
          })}
        </section>
        {isCondo &&
          <>
            <h4>Included in Maintenance Fee</h4>
            <section>
              {getIncludeInMaintenanceFee()?.map((field, index) => {
                if (field) {
                  return (
                    <div key={index}>
                      <span>{field.label}</span>
                      <span>{TryScrambleText(field.value, shouldScramble)}</span>
                    </div>
                  );
                }
              })}
            </section>
            <h4>Utilities</h4>
            <section>
              {getUtilities()?.map((field, index) => {
                if (field) {
                  return (
                    <div key={index}>
                      <span>{field.label}</span>
                      <span>{TryScrambleText(field.value, shouldScramble)}</span>
                    </div>
                  );
                }
              })}
            </section>
          </>
        }
      </DetailsTable>
      <h3 className={styles.extras}>
        Extras
      </h3>
      <div className={styles.description} data-testid={listingPageModalIds.descriptionExtra}>
        {isVisible ? <section>{extras}</section> : <span className='blur'>{TryScrambleText(extras, shouldScramble)}</span> }
      </div>
    </div>
  );
}
